<template>
  <div class="page">
    <!-- Resources Explorer (F1) toggle; only on the standalone /resources page -->
    <div v-if="explorerAvailable" class="explorer-toggle d-flex justify-content-end align-items-center px-3 py-2">
      <b-form-checkbox switch :checked="explorerActive" @change="setExplorerActive">
        {{ explorerActive ? 'Back to classic view' : 'Try the new Resources view' }}
      </b-form-checkbox>
    </div>

    <ResourcesExplorer v-if="explorerActive" />

    <template v-else>
    <!-- Header -->
    <div class="page-header flex-shrink-0 bg-white border-bottom mb-3">
        <div class="d-flex justify-content-between align-items-center px-lg-5 px-md-4 px-3">
          <div>
            <h3 class="mb-2 mr-1">Resources &nbsp;<b-spinner v-if="processing" small label="Small Spinner"></b-spinner></h3>
          </div>
          <div class="d-flex align-items-center">
          <div class="mr-3 btn btn-link text-dark" @click="toggleDisplayType">
            <div><i title="Switch between list and card view" class="float-right fas fa-lg" :class="[displayType == 'card' ? 'fa-list': 'fa-th']"></i></div>
          </div>
          <b-dropdown v-if="!isSpecialFolder" right no-caret size="md" variant="success" toggle-class="text-decoration-none">
            <template #button-content>
              <i class="fas fa-plus"></i><span class="d-md-inline d-none">&nbsp;Add New</span> <i class="fa fa-angle-down"></i>
            </template>
              <span v-if="!isRootFolder || isDocsAndImagesFolder">
              <b-dropdown-item @click="showOrHideDocumentUploadModal(null)" title="Upload Document"><i class="fas fa-file"></i><span class="d-inline ml-2">&nbsp;Add Document</span></b-dropdown-item>
              <b-dropdown-item @click="showOrHideImageUploadModal(null)" title="Upload Image"><i class="fas fa-image"></i><span class="d-inline ml-2">Add Image</span></b-dropdown-item>
              <b-dropdown-item @click="showOrHideVideoUploadModal(null)" title="Upload Video"><i class="fas fa-video"></i><span class="d-inline ml-2">Add Video</span></b-dropdown-item>
              <b-dropdown-divider v-if="currentFolder != null || isDocsAndImagesFolder"></b-dropdown-divider>
              </span>
              <b-dropdown-item @click="showOrHideAddFolderModal(null)" title="Add Folder"><i class="fas fa-folder"></i><span class="d-inline ml-2">Add Folder</span></b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
    </div>

    <!--Folders-->
    <div class="container-fluid px-lg-5 px-md-4 px-3 py-3 bg-white border-bottom mb-3">
      <div class="row no-gutters">
        <div class="col-lg-4 col-md-6">
          <div class="d-inline" v-for="(folder, index) in folderPaths" v-bind:key="folder.id">
            <label class="ml-1" v-if="index > 0">/</label>
            <label v-if="folderPaths.length > 1 && index < folderPaths.length - 1" @click.once="getScopedResources(folder)"><a href="#" >{{folder.name}}</a></label>
            <span v-else>{{folder.name}}</span>
          </div>
        </div>
      </div>
    </div>

    <!--Content-->
    <div class="container-fluid px-lg-5 px-md-4 px-3">
      <div :class="['form-container p-lg-5 p-md-4 p-3', displayType == 'card' ? '' : 'display-list']">
        <!-- Folders -->
        <div class="row" v-if="currentFolder !== null || isRootFolder">
          <div class="col-12">
            <div>
              <div>
                  <p class="mb-2"><strong>Sub Folders</strong></p>
              </div>
            </div>
            <div class="row justify-content-start flex-wrap no-gutters mx-n2" v-if="resources.folders.length > 0">
                <div class="col-xl-3 col-lg-3 col-md-4 col-12 px-1" v-for="(folder) in resources.folders" v-bind:key="folder.id">
                  <b-badge :id="folder.id" v-if="folder.childCount > 0" pill class="position-absolute" variant="info" title="Total count of child folders and files">
                    {{(folder.childCount).toLocaleString()}}
                    <b-tooltip :target="folder.id" non-interactive no-fade>Total number of child folders and documents</b-tooltip>
                  </b-badge>
                  <div class="card document-card d-flex justify-content-between flex-row pl-3 mb-2">
                    <div class="d-flex align-self-center pointer document-title" @click.once="getScopedResources(folder)" :title="folder.name">
                      <div class="mr-2"><i class="fas fa-folder" style="color:rgb(92,92,92)"></i></div>
                      <div class="folder-text">{{folder.name}}</div>
                    </div>
                    <b-dropdown v-if="folder.isEditable" class="flex-right" size="md" variant="link" toggle-class="text-decoration-none" no-caret lazy>
                      <template #button-content>
                      <div class="btn btn-sm px-2"><i class="fas fa-ellipsis-v align-self-center"></i></div><span class="sr-only">Actions</span>
                      </template>
                      <b-dropdown-item href="#" @click.once="showOrHideAddFolderModal(folder)"><i class="fas fa-edit"></i> Edit</b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>
            </div>
            <div class="no-resources d-flex justify-content-center align-items-center p-4 text-black-50" v-if="resources.folders.length == 0">No folders to display</div>
          </div>
        </div>
        <!-- Documents -->
        <div class="row" v-if="(!isRootFolder && !isSpecialFolder) || isDocsAndImagesFolder">
          <div class="col-12">
            <div>
              <div class="divider"></div>
              <div>
                <p class="mb-2"><strong>Documents</strong></p>
              </div>
            </div>
            <div class="row justify-content-start flex-wrap no-gutters mx-n2" v-if="resources.documents.length > 0">
              <div class="col-xl-3 col-lg-3 col-md-4 col-12 px-1" v-for="(document) in resources.documents" v-bind:key="document.id">
                <div class="card document-card d-flex justify-content-between flex-row pl-3 mb-2">
                  <div class="d-flex align-self-center pointer document-title" :title="document.name">
                    <div class="mr-2"><i :class="getCssClassForFile(document.fileName)" :style="`color:${getColorForFile(document.fileName)}`"></i></div>
                    <div class="folder-text">{{document.name}}</div>
                  </div>
                  <b-dropdown class="flex-right" size="md" variant="link" toggle-class="text-decoration-none" no-caret lazy>
                    <template #button-content>
                      <div class="d-flex justify-content-center btn btn-actions px-2 m-0"><i class="fas fa-ellipsis-v align-self-center"></i></div><span class="sr-only">Actions</span>
                    </template>
                    <b-dropdown-item href="#" v-if="false" @click.once="showOrHideDocumentUploadModal(document.id)"><i class="fas fa-edit"></i> Edit</b-dropdown-item>
                    <b-dropdown-item href="#" @click.once="downloadCompanyDocument(document.id)"><i class="fas fa-download"></i> Download</b-dropdown-item>
                      <b-dropdown-item href="#" @click.once="showMoveDocumentToFolderModal(document.id)"><i class="pl-1 fas fa-angle-double-down fa-rotate-270"></i> Move</b-dropdown-item>
                    <b-dropdown-item href="#" @click.once="deleteCompanyDocument(document.id)"><i class="fas fa-trash-alt"></i> Delete</b-dropdown-item>
                    <b-dropdown-item href="#" v-if="canViewQRCode && document.qrCodePublicUrl" @click="showQRCode(document)"><i class="fas fa-qrcode"></i> Get QR Code</b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>
            </div>
            <div class="no-resources d-flex justify-content-center align-items-center p-4 text-black-50" v-if="resources.documents.length == 0">No documents to display</div>
          </div>
        </div>

        <!-- Images -->
        <div class="row mb-4" v-if="(!isRootFolder && !isSpecialFolder) || isDocsAndImagesFolder">
          <div class="col-12">
            <div>
              <div class="divider"></div>
              <div>
                  <p class="mb-2"><strong>Images</strong></p>
              </div>
            </div>
            <div class="row justify-content-start flex-wrap no-gutters mx-n2" v-if="resources.images.length > 0">
              <div class="col-xl-3 col-lg-3 col-md-4 col-12 px-1" v-for="(image, i) in resources.images" v-bind:key="i">
                <div class="card document-card mb-2">
                  <div class="document-image" @click="index = i">
                    <FileThumbnail
                      :thumbnail-url="image.thumbnailPublicUrl"
                      :full-url="image.publicUrl"
                      :alt-text="image.name"
                      file-type="image"
                      img-class="w-100" />
                  </div>
                  <div class="d-flex pl-3 w-100">
                    <div class="d-flex align-self-center pointer document-title" :title="image.name">
                      <div class="folder-text">{{image.name}}</div>
                    </div>
                    <b-dropdown size="md" variant="link" no-caret lazy>
                      <template #button-content>
                      <div class="d-flex justify-content-center btn btn-actions px-2 m-0"><i class="fas fa-ellipsis-v align-self-center"></i></div><span class="sr-only">Actions</span>
                      </template>
                      <b-dropdown-item href="#" v-if="false" @click.once="showOrHideImageUploadModal(image.id)"><i class="fas fa-edit"></i> Edit</b-dropdown-item>
                      <b-dropdown-item href="#" @click.once="downloadCompanyImage(image.id)"><i class="fas fa-download"></i> Download</b-dropdown-item>
                      <b-dropdown-item href="#" @click.once="showMoveImageToFolderModal(image.id)"><i class="pl-1 fas fa-angle-double-down fa-rotate-270"></i> Move</b-dropdown-item>
                      <b-dropdown-item href="#" @click.once="deleteCompanyImage(image.id)"><i class="fas fa-trash-alt"></i> Delete</b-dropdown-item>
                      <b-dropdown-item href="#" v-if="canViewQRCode && image.qrCodePublicUrl" @click="showQRCode(image)"><i class="fas fa-qrcode"></i> Get QR Code</b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>
              </div>
            </div>
            <div class="no-resources d-flex justify-content-center align-items-center p-4 text-black-50" v-if="resources.images.length == 0">No images to display</div>
          </div>
        </div>

        <!-- Videos -->
        <div class="row mb-4" v-if="(!isRootFolder && !isSpecialFolder) || isDocsAndImagesFolder">
          <div class="col-12">
            <div>
              <div class="divider"></div>
              <div>
                  <p class="mb-2"><strong>Videos</strong></p>
              </div>
            </div>
            <div class="row justify-content-start flex-wrap no-gutters mx-n2" v-if="resources.videos.length > 0">
              <div class="col-xl-3 col-lg-3 col-md-4 col-12 px-1" v-for="(video, i) in resources.videos" v-bind:key="i">
                <div class="card document-card mb-2">
                  <div class="document-image">
                    <FileThumbnail
                      :thumbnail-url="video.thumbnailPublicUrl"
                      :full-url="video.publicUrl"
                      :processed-video-url="video.processedVideoPublicUrl"
                      :alt-text="video.name"
                      :processing-status="video.videoProcessingStatus"
                      file-type="video"
                      img-class="w-100"
                      @play="url => openVideoPlayer(url, video)" />
                  </div>
                  <div class="d-flex pl-3 w-100">
                    <div class="d-flex align-self-center pointer document-title" :title="video.name">
                      <div class="folder-text">{{video.name}}</div>
                    </div>
                    <b-dropdown size="md" variant="link" no-caret lazy>
                      <template #button-content>
                      <div class="d-flex justify-content-center btn btn-actions px-2 m-0"><i class="fas fa-ellipsis-v align-self-center"></i></div><span class="sr-only">Actions</span>
                      </template>
                      <b-dropdown-item v-if="false" @click="showOrHideVideoUploadModal(video.id)"><i class="fas fa-edit"></i> Edit</b-dropdown-item>
                      <b-dropdown-item @click="downloadCompanyVideo(video.id)"><i class="fas fa-download"></i> Download</b-dropdown-item>
                      <b-dropdown-item @click="showMoveVideoToFolderModal(video.id)"><i class="pl-1 fas fa-angle-double-down fa-rotate-270"></i> Move</b-dropdown-item>
                      <b-dropdown-item @click="deleteCompanyVideo(video.id)"><i class="fas fa-trash-alt"></i> Delete</b-dropdown-item>
                      <b-dropdown-item v-if="canViewQRCode && video.qrCodePublicUrl" @click="showQRCode(video)"><i class="fas fa-qrcode"></i> Get QR Code</b-dropdown-item>
                    </b-dropdown>
                  </div>
                </div>
              </div>
            </div>
            <div class="no-resources d-flex justify-content-center align-items-center p-4 text-black-50" v-if="resources.videos.length == 0">No videos to display</div>
          </div>
        </div>

        <vue-gallery-slideshow :images="imagesForGallery" :index="index"
                @close="index = null"></vue-gallery-slideshow>
        <slot></slot>
      </div>
    </div>

      <!-- Video Player Modal -->
      <VideoPlayerModal
        :visible.sync="videoPlayerVisible"
        :videos="videoPlaylist"
        :start-index="activeVideoIndex" />

      <!-- QR Code Modal -->
      <QRCodeModal
        v-if="canViewQRCode"
        :visible.sync="qrCodeModalVisible"
        :qr-code-url="activeQRCodeFile && activeQRCodeFile.qrCodePublicUrl"
        :qr-code-svg-url="activeQRCodeFile && activeQRCodeFile.qrCodeSvgPublicUrl"
        :public-share-url="activeQRCodeFile && activeQRCodeFile.publicShareUrl"
        :file-name="activeQRCodeFile && activeQRCodeFile.name"
        :file-type="activeQRCodeFile && activeQRCodeFile.fileType" />

      <!--Folder Modal-->
      <ValidationObserver v-slot="{ invalid }" ref="documentFormValidationObserver">
      <b-modal v-model="folderModalOpen" title="Folder" no-close-on-backdrop no-close-on-esc hide-header-close scrollable>
          <div v-if="folderToSave">
          <b-form v-on:submit.prevent>
              <b-form-group>
              <label>Folder Name</label>
              <ValidationProvider name="Name" rules="required" v-slot="validation" >
              <b-form-input placeholder="Name" v-model="folderToSave.name" :state="spr2_getValidationState(validation)" />
              <b-form-invalid-feedback id="name-feedback">
               {{ validation.errors[0] }}
              </b-form-invalid-feedback>
              </ValidationProvider>
              </b-form-group>
              <b-form-group class="mb-1">
                  <b-form-textarea
                      id="textarea"
                      v-model="folderToSave.notes"
                      placeholder="Notes..."
                      rows="3"
                      max-rows="6"
                      ></b-form-textarea>
              </b-form-group>
              <b-form-group>
                <b-checkbox v-model="folderToSave.isArchived" :disabled="folderToSave.id == null">Archived</b-checkbox>
              </b-form-group>
          </b-form>
          </div>
          <template v-slot:modal-footer>
              <b-btn variant="light" @click="showOrHideAddFolderModal(null)">Close</b-btn>
              <b-btn variant="primary" :disabled="invalid" @click="doSaveFolder" class="float-right ml-1">Save</b-btn>
          </template>
      </b-modal>
      </ValidationObserver>

      <!-- Document Upload Modal -->
      <FileUploadModal
        v-model="documentUploadModalOpen"
        file-type="document"
        :accepted-file-types="acceptedDocumentTypes"
        :document-types-options="documentTypesOptions"
        :processing="processing"
        :event-id="eventId"
        :current-folder="currentFolder"
        @upload="handleDocumentUpload"
        @modal-hidden="onDocumentModalHidden"
      />

      <!-- Image Upload Modal -->
      <FileUploadModal
        v-model="imageUploadModalOpen"
        file-type="image"
        :accepted-file-types="acceptedImageTypes"
        :processing="processing"
        :event-id="eventId"
        :current-folder="currentFolder"
        @upload="handleImageUpload"
        @modal-hidden="onImageModalHidden"
      />

      <!-- Video Upload Modal -->
      <FileUploadModal
        v-model="videoUploadModalOpen"
        file-type="video"
        :accepted-file-types="acceptedVideoTypes"
        :processing="processing"
        :event-id="eventId"
        :current-folder="currentFolder"
        @upload="handleVideoUpload"
        @modal-hidden="onVideoModalHidden"
      />

      <!--Move Document to Folder Modal-->
      <b-modal v-model="moveDocumentTofolderModalOpen" title="Move Document" no-close-on-backdrop no-close-on-esc hide-header-close scrollable>
        <div class="p-3">
          <div class="form">
            <b-form @submit.prevent="moveDocumentToFolder">
              <div class="form-group row">
                <div class="col-12">
                  <h6 class="mb-3">Current Folder: {{this.currentDocumentPath}}</h6>
                </div>
              </div>
              <div class="form-group row">
                <div class="col-12">
                  <h6 class="mb-3">New Folder</h6>
                  <b-form-select :options="foldersForMove" v-model="folderIdForMove">
                    <template #first>
                      <b-form-select-option :value="null" disabled>-- Choose a new folder location --</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>
              </div>
            </b-form>
          </div>
        </div>
        <template v-slot:modal-footer>
            <b-btn variant="light" @click="moveDocumentTofolderModalOpen = false">Close</b-btn>
            <b-btn type="submit" variant="primary" @click="moveDocumentToFolder" :disabled="folderIdForMove == null" class="float-right ml-1">Move</b-btn>
        </template>
      </b-modal>

      <!--Move Image to Folder Modal-->
      <b-modal v-model="moveImageTofolderModalOpen" title="Move Image" no-close-on-backdrop no-close-on-esc hide-header-close scrollable>
        <div class="p-3">
          <div class="form">
            <b-form @submit.prevent="moveImageToFolder">
              <div class="form-group row">
                <div class="col-12">
                  <h6 class="mb-3">Current Folder: {{this.currentImagePath}}</h6>
                </div>
              </div>
              <div class="form-group row">
                <div class="col-12">
                  <h6 class="mb-3">New Folder</h6>
                  <b-form-select :options="foldersForMove" v-model="folderIdForMove">
                    <template #first>
                      <b-form-select-option :value="null" disabled>-- Choose a new folder location --</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>
              </div>
            </b-form>
          </div>
        </div>
        <template v-slot:modal-footer>
            <b-btn variant="light" @click="moveImageTofolderModalOpen = false">Close</b-btn>
            <b-btn type="submit" variant="primary" @click="moveImageToFolder" :disabled="folderIdForMove == null" class="float-right ml-1">Move</b-btn>
        </template>
      </b-modal>

        <!--Move Video to Folder Modal-->
        <b-modal v-model="moveVideoTofolderModalOpen" title="Move Video" no-close-on-backdrop no-close-on-esc hide-header-close scrollable>
        <div class="p-3">
          <div class="form">
            <b-form @submit.prevent="moveVideoToFolder">
              <div class="form-group row">
                <div class="col-12">
                  <h6 class="mb-3">Current Folder: {{this.currentVideoPath}}</h6>
                </div>
              </div>
              <div class="form-group row">
                <div class="col-12">
                  <h6 class="mb-3">New Folder</h6>
                  <b-form-select :options="foldersForMove" v-model="folderIdForMove">
                    <template #first>
                      <b-form-select-option :value="null" disabled>-- Choose a new folder location --</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>
              </div>
            </b-form>
          </div>
        </div>
        <template v-slot:modal-footer>
            <b-btn variant="light" @click="moveVideoTofolderModalOpen = false">Close</b-btn>
            <b-btn type="submit" variant="primary" @click="moveVideoToFolder" :disabled="folderIdForMove == null" class="float-right ml-1">Move</b-btn>
        </template>
      </b-modal>
    </template>
    </div>
</template>
<script>

import { mapActions } from 'vuex'
import { spr2Procedures, spr2Validation } from '../mixins/Spr2SharedFunctions'
import VueGallerySlideshow from 'vue-gallery-slideshow'
import FileUploadModal from '../components/FileUploadModal.vue'
import FileThumbnail from '../components/FileThumbnail.vue'
import VideoPlayerModal from '../components/VideoPlayerModal.vue'
import QRCodeModal from '../components/QRCodeModal.vue'
import publicFilesApi from '../api/publicFilesApi'
import ResourcesExplorer from '../components/resources-explorer/ResourcesExplorer.vue'
import { readToggle, writeToggle } from '../components/resources-explorer/resourcesData'
import { hasActiveUploads, cancelAll as cancelAllUploads } from '../components/resources-explorer/uploadQueue'

// component code
export default {
  name: 'Resources',
  props: {
    eventId: { type: String },
    folderId: { type: String }
  },
  mixins: [ spr2Procedures, spr2Validation ],
  components: {
    VueGallerySlideshow,
    FileUploadModal,
    FileThumbnail,
    VideoPlayerModal,
    QRCodeModal,
    ResourcesExplorer
  },
  data () {
    return {
      processing: false,
      resources: {folders: [], documents: [], images: [], videos: []},
      folderPaths: [],
      currentFolder: null,
      folderToSave: null,
      folderModalOpen: false,
      documentUploadModalOpen: false,
      imageUploadModalOpen: false,
      videoUploadModalOpen: false,
      moveDocumentTofolderModalOpen: false,
      moveImageTofolderModalOpen: false,
      moveVideoTofolderModalOpen: false,
      displayType: 'card',
      documentForUpload: { id: null, name: null, file: null, documentTypeId: '', folderId: null, note: null },
      imageForUpload: { id: null, name: null, file: null, folderId: null, note: null },
      videoForUpload: { id: null, name: null, file: null, folderId: null, note: null },
      foldersForMove: [],
      acceptedDocumentTypes: [],
      acceptedImageTypes: [],
      acceptedVideoTypes: [],
      currentDocumentPath: null,
      currentDocumentId: null,
      currentImagePath: null,
      currentImageId: null,
      currentVideoPath: null,
      currentVideoId: null,
      folderIdForMove: null,
      index: null,
      videoPlayerVisible: false,
      activeVideoIndex: 0,
      qrCodeModalVisible: false,
      activeQRCodeFile: null,
      videoPollingInterval: null,
      explorerEnabled: readToggle(),
      classicLoaded: false
    }
  },
  created () {
    if (!this.explorerActive) {
      this.loadData()
    }
  },

  beforeDestroy () {
    if (this.videoPollingInterval) {
      clearInterval(this.videoPollingInterval)
      this.videoPollingInterval = null
    }
  },
  // Resources Explorer (F4a): folder navigation only changes the query (no leave), so this
  // fires only when leaving the Resources page itself.
  beforeRouteLeave (to, from, next) {
    if (to.path === from.path || !hasActiveUploads()) {
      next()
      return
    }
    this.$bvModal.msgBoxConfirm('Uploads are still in progress. Leave and cancel them?', {
      title: 'Leave Resources?',
      okTitle: 'Leave',
      okVariant: 'danger',
      cancelTitle: 'Stay',
      centered: true
    }).then(function (ok) {
      if (ok) {
        cancelAllUploads()
        next()
      } else {
        next(false)
      }
    }, function () {
      next(false)
    })
  },
  computed: {
    // The explorer is only offered on the standalone /resources page (not when embedded in events)
    explorerAvailable () {
      return this.eventId === undefined || this.eventId === null
    },
    explorerActive () {
      return this.explorerAvailable && this.explorerEnabled
    },
    qrCodeFeatureEnabled () {
      return this.$store.getters['featureFlags/isEnabled']('media.qrcode-sharing')
    },
    canViewQRCode () {
      return this.qrCodeFeatureEnabled &&
             (this.$store.state.loggedInUserPermissions && this.$store.state.loggedInUserPermissions.viewQRCode === true)
    },
    documentTypesOptions () {
      var project = (o, p) => {
        return { text: o.name, value: o.id }
      }
      var types = this.$store.state.events.documentTypes
      var results = []

      for (var i = 0; i < types.length; i++) {
        var obj = types[i]
        var projection = { text: 1, value: 1 }
        var projectedObj = project(obj, projection)
        console.log(projectedObj)
        results.push(projectedObj)
      }
      console.log(
        'Returning Document Types Options: ' + JSON.stringify(results, null, 2)
      )
      return results
    },
    isDocsAndImagesFolder () {
      return this.eventId !== undefined // || this.currentFolder !== null || this.currentFolder.id !== null
    },
    isRootFolder () {
      return this.currentFolder === undefined ||
             this.currentFolder === null ||
             this.currentFolder.id === null ||
             this.currentFolder.id === -1
    },
    isSpecialFolder () {
      let isFolderSpecial = this.currentFolder !== null &&
        this.currentFolder.folderType.toLowerCase() === 'property' &&
        this.currentFolder.propertyId === null &&
        this.currentFolder.isEditable === false

      return isFolderSpecial
    },
    acceptedFileExtensionsWithDot () {
      return this.acceptedDocumentTypes.map(a => `.${a.ext}`).join(',')
    },
    acceptedFileExtensions () {
      return this.acceptedDocumentTypes.map(a => a.ext).join(',')
    },
    acceptedImageExtensionsWithDot () {
      return this.acceptedImageTypes.map(a => `.${a.ext}`).join(',')
    },
    acceptedImageExtensions () {
      return this.acceptedImageTypes.map(a => a.ext).join(',')
    },
    acceptedVideoExtensionsWithDot () {
      return this.acceptedVideoTypes.map(a => `.${a.ext}`).join(',')
    },
    acceptedVideoExtensions () {
      return this.acceptedVideoTypes.map(a => a.ext).join(',')
    },
    imagesForGallery () {
      let images = this.resources.images.length > 0 ? this.resources.images : []
      console.log(images)
      return images.map((image) => {
        return {
          url: image.publicUrl,
          id: image.id,
          alt: (image.notes || image.name)
        }
      })
    },

    videoPlaylist () {
      return (this.resources.videos || []).map(v => ({
        url: v.processedVideoPublicUrl || v.publicUrl,
        // Always use video/mp4 for processed videos — MediaConvert always outputs MP4.
        // The original contentType (e.g. video/quicktime for .MOV) must NOT be used
        // for the transcoded output or Chrome/Firefox will refuse to play it.
        contentType: v.processedVideoPublicUrl ? 'video/mp4' : (v.contentType || 'video/mp4'),
        name: v.name,
        thumbnailUrl: v.thumbnailPublicUrl
      }))
    }
  },
  methods: {
    ...mapActions('resources', ['getResources', 'getResourcesByFolderId',
      'saveFolder', 'getFolderForEdit',
      'uploadDocument', 'downloadDocument', 'deleteDocument',
      'uploadImage', 'downloadImage', 'deleteImage',
      'uploadVideo', 'downloadVideo', 'deleteVideo',
      'getAcceptedFileTypes',
      'getMoveFoldersForDocument', 'getMoveFoldersForImage', 'getMoveFoldersForVideo',
      'moveDocument', 'moveImage', 'moveVideo']),
    setExplorerActive (on) {
      this.explorerEnabled = on === true
      writeToggle(this.explorerEnabled)
      if (!this.explorerEnabled && !this.classicLoaded) {
        this.loadData()
      }
    },
    loadData () {
      this.classicLoaded = true
      this.initializeDocumentForUpload()
      this.initializeImageForUpload()
      this.initializeVideoForUpload()
      this.getScopedResources(null)

      this.getAcceptedFileTypes().then((types) => {
        this.acceptedDocumentTypes = types.filter((item) => { return item.type === 1 })
        this.acceptedImageTypes = types.filter((item) => { return item.type === 2 })
        this.acceptedVideoTypes = types.filter((item) => { return item.type === 3 })
      })
    },
    getScopedResources (folder) {
      console.log('path click or initial load: ', this.eventId)
      this.processing = true
      this.currentFolder = folder || null
      this.buildFolderPaths(folder)

      if (folder === null || folder.id === -1) {
        this.getResources({eventId: this.eventId}).then(result => {
          this.resources = result
          this.processing = false
          this.startVideoProcessingPolling()

          if (this.folderId != null && folder === null) {
            let f = this.resources.folders.find((ref) => ref.id === this.folderId)
            if (f != null) {
              this.getScopedResources(f)
            }
          }
        })
      } else {
        this.getResourcesByFolderId({folderId: folder.id, folderType: folder.folderType, propertyId: folder.propertyId, eventId: this.eventId}).then(result => {
          this.resources = result
          this.processing = false
          this.startVideoProcessingPolling()
        })
      }
    },
    buildFolderPaths (folder) {
      if (folder === null) { // Rebuild paths from root
        this.folderPaths = []
        this.folderPaths.push({id: -1, name: 'Root Folders', folderType: 'Company', propertyId: null, isEditable: false})
      } else { // Update paths from folderId
        let currentFolder = this.resources.folders.find((item) => {
          return item.id === folder.id
        }) || null

        if (currentFolder === null) {
          let folderIndex = this.folderPaths.findIndex((item) => {
            return item.id === folder.id
          }) || null
          this.folderPaths = this.folderPaths.slice(0, folderIndex + 1)
        } else {
          this.folderPaths.push({id: currentFolder.id, name: currentFolder.name, folderType: currentFolder.folderType, propertyId: currentFolder.propertyId, isEditable: currentFolder.isEditable})
        }
      }
    },
    showOrHideAddFolderModal (folder) {
      if (this.folderModalOpen) {
        this.folderModalOpen = false
        this.folderForEdit = null
      } else {
        this.folderModalOpen = true
        if (folder === null) {
          let parentId = this.currentFolder !== null ? this.currentFolder.id : null
          let propertyId = this.currentFolder !== null ? this.currentFolder.propertyId : null
          this.folderToSave = {id: null, name: null, notes: null, parentId: parentId, propertyId: propertyId}
        } else {
          this.getFolderForEdit({folderId: folder.id, propertyId: folder.propertyId}).then(folder => {
            this.folderToSave = folder
          })
        }
      }
    },
    toggleDisplayType () {
      this.displayType = (this.displayType === 'list') ? 'card' : 'list'
    },
    doSaveFolder () {
      this.processing = true
      this.folderToSave.eventId = this.eventId
      console.log('TO SAVE: ', this.folderToSave)
      this.saveFolder({folder: this.folderToSave}).then((result) => {
        this.processing = false
        if (result.success) {
          this.folderModalOpen = false
          this.getScopedResources(this.currentFolder)
        } else {
          this.$toasted.error(result.failureReason, { duration: 4000 })
        }
      })
    },
    showOrHideDocumentUploadModal (documentId) {
      this.initializeDocumentForUpload(documentId)
      this.documentUploadModalOpen = !this.documentUploadModalOpen
    },
    showMoveDocumentToFolderModal (documentId) {
      this.foldersForMove = []
      this.currentDocumentPath = null
      this.currentDocumentId = documentId
      this.moveDocumentTofolderModalOpen = true
      this.folderIdForMove = null

      let document = this.resources.documents.find((item) => {
        return item.id === documentId
      }) || null

      if (document !== null) {
        this.getMoveFoldersForDocument({documentId: document.id, propertyId: document.propertyId, eventId: document.eventId}).then(result => {
          var project = (o, p) => {
            return { text: o.path, value: o.id }
          }
          for (var i = 0; i < result.data.folders.length; i++) {
            var obj = result.data.folders[i]
            var projection = { text: 1, value: 1 }
            if (obj.isCurrentlyAssignedFolder) {
              this.currentDocumentPath = obj.path
            } else {
              var projectedObj = project(obj, projection)
              this.foldersForMove.push(projectedObj)
            }
          }
        })
      }
    },
    initializeDocumentForUpload (documentId) {
      if (documentId !== null) {
        let d = this.resources.documents.find((item) => {
          return item.id === documentId
        }) || null
        if (d !== null) {
          this.documentForUpload = Object.assign({}, { id: d.documentId, name: d.name, file: null, folderId: d.folderId, note: d.note })
        }
      } else {
        this.documentForUpload = { id: documentId, name: null, file: null, folderId: null, note: null }
      }
    },
    showOrHideImageUploadModal (imageId) {
      this.initializeImageForUpload(imageId)
      this.imageUploadModalOpen = !this.imageUploadModalOpen
    },
    showMoveImageToFolderModal (imageId) {
      this.foldersForMove = []
      this.currentImagePath = null
      this.currentImageId = imageId
      this.moveImageTofolderModalOpen = true
      this.folderIdForMove = null

      let image = this.resources.images.find((item) => {
        return item.id === imageId
      }) || null

      if (image !== null) {
        this.getMoveFoldersForImage({imageId: image.id, propertyId: image.propertyId, eventId: image.eventId}).then(result => {
          var project = (o, p) => {
            return { text: o.path, value: o.id }
          }
          for (var i = 0; i < result.data.folders.length; i++) {
            var obj = result.data.folders[i]
            var projection = { text: 1, value: 1 }
            if (obj.isCurrentlyAssignedFolder) {
              this.currentImagePath = obj.path
            } else {
              var projectedObj = project(obj, projection)
              this.foldersForMove.push(projectedObj)
            }
          }
        })
      }
    },
    initializeImageForUpload (imageId) {
      if (imageId !== null) {
        let image = this.resources.images.find((item) => {
          return item.id === imageId
        }) || null
        if (image !== null) {
          this.imageForUpload = Object.assign({}, { id: image.id, name: image.name, file: null, folderId: image.folderId })
        }
      } else {
        this.imageForUpload = { id: imageId, name: null, file: null, folderId: null, note: null }
      }
    },
    showOrHideVideoUploadModal (videoId) {
      this.initializeVideoForUpload(videoId)
      this.videoUploadModalOpen = !this.videoUploadModalOpen
    },
    showMoveVideoToFolderModal (videoId) {
      this.foldersForMove = []
      this.currentVideoPath = null
      this.currentVideoId = videoId
      this.moveVideoTofolderModalOpen = true
      this.folderIdForMove = null

      let video = this.resources.videos.find((item) => {
        return item.id === videoId
      }) || null

      if (video !== null) {
        this.getMoveFoldersForVideo({videoId: video.id, propertyId: video.propertyId, eventId: video.eventId}).then(result => {
          var project = (o, p) => {
            return { text: o.path, value: o.id }
          }
          for (var i = 0; i < result.data.folders.length; i++) {
            var obj = result.data.folders[i]
            var projection = { text: 1, value: 1 }
            if (obj.isCurrentlyAssignedFolder) {
              this.currentVideoPath = obj.path
            } else {
              var projectedObj = project(obj, projection)
              this.foldersForMove.push(projectedObj)
            }
          }
        })
      }
    },
    initializeVideoForUpload (videoId) {
      if (videoId !== null) {
        let video = this.resources.videos.find((item) => {
          return item.id === videoId
        }) || null
        if (video !== null) {
          this.videoForUpload = Object.assign({}, { id: video.id, name: video.name, file: null, folderId: video.folderId })
        }
      } else {
        this.videoForUpload = { id: videoId, name: null, file: null, folderId: null, note: null }
      }
    },
    fileSelectionChanged (file) {
      if (file !== null) {
        this.documentForUpload.name = file.name
        this.documentForUpload.fileName = file.name
      }
    },
    imageSelectionChanged (file) {
      if (file !== null) {
        this.imageForUpload.name = file.name
        this.imageForUpload.fileName = file.name
      }
    },
    videoSelectionChanged (file) {
      if (file !== null) {
        this.videoForUpload.name = file.name
        this.videoForUpload.fileName = file.name
      }
    },
    readyForUpload (invalid) {
      return (invalid === false && this.processing === false)
    },
    // Documents
    uploadDocumentForCompany () {
      if (this.currentFolder !== null) {
        this.documentForUpload.folderId = this.currentFolder.id
        this.documentForUpload.folderType = this.currentFolder.folderType
        this.documentForUpload.propertyId = this.currentFolder.propertyId
      }
      this.documentForUpload.eventId = this.eventId
      this.processing = true
      this.uploadDocument(this.documentForUpload).then(result => {
        this.documentTypeId = null
        this.showOrHideDocumentUploadModal()
        this.getScopedResources(this.currentFolder)
        this.processing = false
      })
    },
    
    // New reusable component handlers
    handleDocumentUpload (uploadData) {
      this.processing = true
      this.uploadDocument(uploadData).then(result => {
        this.documentTypeId = null
        this.showOrHideDocumentUploadModal(null)
        this.getScopedResources(this.currentFolder)
        this.processing = false
      })
    },
    
    handleImageUpload (uploadData) {
      this.processing = true
      this.uploadImage(uploadData).then(result => {
        this.documentTypeId = null
        this.showOrHideImageUploadModal(null)
        this.getScopedResources(this.currentFolder)
        this.processing = false
      })
    },
    
    handleVideoUpload (uploadData) {
      this.processing = true
      this.uploadVideo(uploadData).then(result => {
        this.videoTypeId = null
        this.showOrHideVideoUploadModal(null)
        this.getScopedResources(this.currentFolder)
        this.processing = false
      })
    },
    
    onDocumentModalHidden () {
      // Reset form when modal is hidden
      this.initializeDocumentForUpload()
    },
    
    onImageModalHidden () {
      // Reset form when modal is hidden
      this.initializeImageForUpload()
    },
    
    onVideoModalHidden () {
      // Reset form when modal is hidden
      this.initializeVideoForUpload()
    },
    downloadCompanyDocument (documentId) {
      let document = this.resources.documents.find((item) => {
        return item.id === documentId
      }) || null
      if (document !== null) {
        this.downloadDocument({documentId: document.id, propertyId: document.propertyId, fileName: document.name, eventId: document.eventId})
      }
    },
    async deleteCompanyDocument (documentId) {
      let document = this.resources.documents.find((item) => {
        return item.id === documentId
      }) || null
      if (document !== null) {
        if (confirm(`Are you sure you want to delete, "${document.name}"?`)) {
          console.log(this.currentFolder)
          let result = await this.deleteDocument({documentId: document.id, propertyId: document.propertyId, fileName: document.name, eventId: document.eventId, name: document.name})
          if (result.success === true) {
            this.getScopedResources(this.currentFolder)
            this.$toasted.success('Document deleted successfully', { duration: 4000 })
          }
        }
      }
    },
    moveDocumentToFolder () {
      let document = this.resources.documents.find((item) => {
        return item.id === this.currentDocumentId
      }) || null
      this.moveDocumentTofolderModalOpen = false

      this.moveDocument({documentId: document.id, propertyId: document.propertyId, eventId: document.eventId, folderId: this.folderIdForMove}).then(result => {
        if (result.data.success) {
          this.getScopedResources(this.currentFolder)
          this.$toasted.success(`Document successfully moved`, { duration: 4000 })
        } else {
          this.$toasted.error(result.data.failureReason, { duration: 4000 })
        }
        this.folderIdForMove = null
        this.currentDocumentId = null
        this.currentDocumentPath = null
      })
    },
    // Images
    uploadImageForCompany () {
      if (this.currentFolder !== null) {
        this.imageForUpload.folderId = this.currentFolder.id
        this.imageForUpload.folderType = this.currentFolder.folderType
        this.imageForUpload.propertyId = this.currentFolder.propertyId
      }
      this.imageForUpload.eventId = this.eventId
      this.processing = true

      this.uploadImage(this.imageForUpload).then(result => {
        this.documentTypeId = null
        this.showOrHideImageUploadModal()
        this.getScopedResources(this.currentFolder)
        this.processing = false
      })
    },
    downloadCompanyImage (imageId) {
      let image = this.resources.images.find((item) => {
        return item.id === imageId
      }) || null
      if (image !== null) {
        this.downloadImage({imageId: image.id, propertyId: image.propertyId, fileName: image.name, eventId: image.eventId})
      }
    },
    async deleteCompanyImage (imageId) {
      this.imageForUpload.eventId = this.eventId
      let image = this.resources.images.find((item) => {
        return item.id === imageId
      }) || null
      if (image !== null) {
        if (confirm(`Are you sure you want to delete, "${image.name}"?`)) {
          console.log(this.currentFolder)
          let result = await this.deleteImage({imageId: image.id, propertyId: image.propertyId, eventId: image.eventId, name: image.name})
          if (result.success === true) {
            this.getScopedResources(this.currentFolder)
            this.$toasted.success('Image deleted successfully', { duration: 4000 })
          }
        }
      }
    },
    moveImageToFolder () {
      let image = this.resources.images.find((item) => {
        return item.id === this.currentImageId
      }) || null
      this.moveImageTofolderModalOpen = false

      this.moveImage({imageId: image.id, propertyId: image.propertyId, eventId: image.eventId, folderId: this.folderIdForMove}).then(result => {
        if (result.data.success) {
          this.getScopedResources(this.currentFolder)
          this.$toasted.success(`Image successfully moved`, { duration: 4000 })
        } else {
          this.$toasted.error(result.data.failureReason, { duration: 4000 })
        }
        this.folderIdForMove = null
        this.currentImageId = null
        this.currentImagePath = null
      })
    },
    // Videos
    uploadVideoForCompany () {
      if (this.currentFolder !== null) {
        this.videoForUpload.folderId = this.currentFolder.id
        this.videoForUpload.folderType = this.currentFolder.folderType
        this.videoForUpload.propertyId = this.currentFolder.propertyId
      }
      this.videoForUpload.eventId = this.eventId
      this.processing = true

      this.uploadVideo(this.videoForUpload).then(result => {
        this.videoTypeId = null
        this.showOrHideVideoUploadModal()
        this.getScopedResources(this.currentFolder)
        this.processing = false
      })
    },
    downloadCompanyVideo (videoId) {
      let video = this.resources.videos.find((item) => {
        return item.id === videoId
      }) || null
      if (video !== null) {
        this.downloadVideo({videoId: video.id, propertyId: video.propertyId, fileName: video.name, eventId: video.eventId})
      }
    },
    async deleteCompanyVideo (videoId) {
      this.videoForUpload.eventId = this.eventId
      let video = this.resources.videos.find((item) => {
        return item.id === videoId
      }) || null
      if (video !== null) {
        if (confirm(`Are you sure you want to delete, "${video.name}"?`)) {
          console.log(this.currentFolder)
          let result = await this.deleteVideo({videoId: video.id, propertyId: video.propertyId, eventId: video.eventId, name: video.name})
          if (result.success === true) {
            this.getScopedResources(this.currentFolder)
            this.$toasted.success('Video deleted successfully', { duration: 4000 })
          }
        }
      }
    },
    moveVideoToFolder () {
      let video = this.resources.videos.find((item) => {
        return item.id === this.currentVideoId
      }) || null
      this.moveVideoTofolderModalOpen = false

      this.moveVideo({videoId: video.id, propertyId: video.propertyId, eventId: video.eventId, folderId: this.folderIdForMove}).then(result => {
        if (result.data.success) {
          this.getScopedResources(this.currentFolder)
          this.$toasted.success(`Video successfully moved`, { duration: 4000 })
        } else {
          this.$toasted.error(result.data.failureReason, { duration: 4000 })
        }
        this.folderIdForMove = null
        this.currentVideoId = null
        this.currentVideoPath = null
      })
    },
    getCssClassForFile (fileName) {
      let extension = fileName.split('.').pop()
      var type = this.acceptedDocumentTypes.find((item) => { return item.ext === extension.toLowerCase() }) || null
      return (type !== null) ? type.class : 'fas fa-file'
    },
    getColorForFile (fileName) {
      let extension = fileName.split('.').pop()
      var type = this.acceptedDocumentTypes.find((item) => { return item.ext === extension.toLowerCase() }) || null
      return (type !== null) ? type.color : 'rgb(0,0,0)'
    },

    openVideoPlayer (url, video) {
      this.activeVideoIndex = (this.resources.videos || []).indexOf(video)
      // Use false → $nextTick → true to ensure any prior modal state (e.g. a just-closed
      // QR code modal returning focus to the dropdown) is fully resolved before opening.
      this.videoPlayerVisible = false
      this.$nextTick(() => {
        this.videoPlayerVisible = true
      })
    },

    showQRCode (file) {
      // Reset visibility first so BootstrapVue's b-modal watcher always sees
      // a false → true transition, even when reopening immediately after close.
      this.qrCodeModalVisible = false
      this.$nextTick(() => {
        this.activeQRCodeFile = file
        this.qrCodeModalVisible = true
      })
    },

    startVideoProcessingPolling () {
      // Clear any existing poll
      if (this.videoPollingInterval) {
        clearInterval(this.videoPollingInterval)
        this.videoPollingInterval = null
      }

      const pendingVideos = (this.resources.videos || []).filter(
        v => v.videoProcessingStatus === 'Pending' || v.videoProcessingStatus === 'Processing'
      )
      if (pendingVideos.length === 0) return

      const companyId = this.$store.state.userCurrentCompany && this.$store.state.userCurrentCompany.companyId

      this.videoPollingInterval = setInterval(async () => {
        for (const video of pendingVideos) {
          try {
            const res = await publicFilesApi.getVideoProcessingStatus(companyId, video.id)
            const s = res.data
            if (s.status === 'Complete') {
              video.thumbnailPublicUrl = s.thumbnailPublicUrl || video.thumbnailPublicUrl
              video.processedVideoPublicUrl = s.processedVideoPublicUrl || video.publicUrl
              video.videoProcessingStatus = 'Complete'
            } else if (s.status === 'Failed') {
              video.videoProcessingStatus = 'Failed'
            }
          } catch (e) { /* ignore polling errors — retry next interval */ }
        }

        const stillPending = (this.resources.videos || []).some(
          v => v.videoProcessingStatus === 'Pending' || v.videoProcessingStatus === 'Processing'
        )
        if (!stillPending) {
          clearInterval(this.videoPollingInterval)
          this.videoPollingInterval = null
        }
      }, 10000) // poll every 10 seconds
    }
  }
}

</script>

<style scoped>
.explorer-toggle {
  background: #fff;
  border-bottom: 1px solid #e2e5ea;
  font-size: 14px;
}
.page-header {
  z-index: 10;
  overflow: visible;
}
.form-container {
  height: calc(100vh - 170px);
  overflow-y: auto;
  overflow-x: hidden;
}
.pointer {
  cursor: context-menu;
}

.folder-text {
  width:185px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin:0;
}

.document-title {
  width: calc(100% - 50px);
  margin-top:10px;
  margin-bottom: 10px;
}

.document-image {
  width: 100%;
  object-fit: cover;
  height: 100px;
  overflow: hidden;
  border-radius: 10px 10px 0 0;
  background: #333;
}

.document-card {
  transform: none;
}

.document-card:hover {
  background: #f5f5f5;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  text-decoration: none;
}

.document-card .btn:hover {
  background: rgba(0,0,0,0.1);
}

.no-resources {
  border: 1px dashed #ccc;
  border-radius: 10px;
  background: #f9f9f9;
}

.divider {
  margin-top: 2.5em;
  margin-bottom: 2.5em;
  display: table;
  width: 100%;
  border-top: 1px solid #f5f5f5;
}

.display-list .col-xl-3,
.display-list .col-lg-3,
.display-list .col-md-4{
  flex: 0 0 100% !important;
  max-width: 100% !important;
}

.display-list .document-card {
  display: flex;
  flex-direction: row;
}

.display-list .document-image {
  width: 80px;
  height: 50px;
  border-radius: 10px 0 0 10px;
}

.display-list .document-image img{
  object-fit: cover;
  height: 100%;
}

.display-list .document-card .folder-text {
  width: auto;
}

.red {
  color:red;
}

.badge-pill {
  top: -5px;
  right: 1px;
  z-index: 100;
  line-height: 1;
  padding: 3px 5px 2px 5px;
}
</style>
